import { ConfirmationModal, MMButton, RecurringStatusBadge } from '@components'
import cn from '@integrations/classnames'
import toast from '@integrations/notifications'
import { useContext } from '@integrations/react'
import { formatDisplayDate, getRecurringFrequencyDescription } from '@utils'
import { RecurringPlanStatuses, useRecurringPlanCancelQry, useRecurringPlanStatusQry } from 'mmfintech-backend-api'
import type { PortalRecurringPlanView } from 'mmfintech-backend-api/types'
import { formatMoney, GlobalContext, tr } from 'mmfintech-commons'
import { ErrorDisplay } from 'mmfintech-portal-commons'
import styles from './RecurringEditModal.module.scss'
import { RecurringModalCard, RecurringModalSurface } from './RecurringModalSurface'

interface Props {
  plan: PortalRecurringPlanView
  reload: () => void
}

function RecurringEditModal({ plan, reload }: Props) {
  const { modalShow, modalHide } = useContext(GlobalContext)

  const { updatePlanStatus, updatePlanStatusError, updatePlanStatusLoading } = useRecurringPlanStatusQry()
  const { cancelPlan, cancelPlanError, cancelPlanLoading } = useRecurringPlanCancelQry()

  const {
    id,
    recurringPlanName,
    startDate,
    recurringStep,
    recurringPlanStatus,
    nextBillingDate,
    recurringAmount,
    currency,
    recipient,
    accountName
  } = plan || {}

  const isPaused = recurringPlanStatus === RecurringPlanStatuses.PAUSED
  const isCancelled = recurringPlanStatus === RecurringPlanStatuses.CANCELLED

  function handleToggleStatus() {
    const nextStatus = isPaused ? RecurringPlanStatuses.ACTIVE : RecurringPlanStatuses.PAUSED
    updatePlanStatus(id, nextStatus, () => {
      reload()
      modalHide()
      toast.success(
        isPaused
          ? tr('FRONTEND.RECURRING.EDIT.RESUME_SUCCESS', 'Recurring payment resumed')
          : tr('FRONTEND.RECURRING.EDIT.PAUSE_SUCCESS', 'Recurring payment paused')
      )
    })
  }

  function handleCancelConfirmed() {
    cancelPlan(id, () => {
      reload()
      modalHide()
      toast.success(tr('FRONTEND.RECURRING.EDIT.CANCEL_SUCCESS', 'Recurring payment cancelled'))
    })
  }

  function handleCancelClick() {
    modalShow({
      content: (
        <ConfirmationModal
          title={tr('FRONTEND.RECURRING.EDIT.CANCEL_CONFIRM_TITLE', 'Cancel recurring payment?')}
          question={tr(
            'FRONTEND.RECURRING.EDIT.CANCEL_CONFIRM_QUESTION',
            'This will permanently stop this recurring payment and cannot be undone.'
          )}
          confirmCaption={tr('FRONTEND.RECURRING.EDIT.CANCEL_CONFIRM_BUTTON', 'Cancel payment')}
          onConfirm={handleCancelConfirmed}
          onClose={modalHide}
        />
      )
    })
  }

  return (
    <RecurringModalSurface
      title={tr('FRONTEND.RECURRING.EDIT.TITLE', 'Edit recurring payment')}
      className={cn('recurring-edit-modal-wrapper')}
      data-test='recurring-edit-modal'
    >
      <RecurringModalCard amount className={cn('recurring-details-card recurring-details-amount-card')}>
        <div className={cn('amount')} data-test='recurring-edit-amount'>
          {formatMoney(recurringAmount, currency)}
        </div>
        <RecurringStatusBadge status={recurringPlanStatus} />
      </RecurringModalCard>

      <RecurringModalCard className={cn('recurring-details-card')}>
        <div className={cn('row')}>
          <span className={cn('label')}>{tr('FRONTEND.RECURRING.EDIT.NAME', 'Recurring Plan Name')}</span>
          <span className={cn('value')}>{recurringPlanName}</span>
        </div>
        <div className={cn('row')}>
          <span className={cn('label')}>{tr('FRONTEND.RECURRING.EDIT.RECIPIENT', 'Recipient')}</span>
          <span className={cn('value')}>
            {recipient} · {accountName}
          </span>
        </div>
        <div className={cn('row')}>
          <span className={cn('label')}>{tr('FRONTEND.RECURRING.EDIT.FREQUENCY', 'Frequency')}</span>
          <span className={cn('value')} data-test='recurring-edit-frequency'>
            {getRecurringFrequencyDescription(startDate, recurringStep)}
            {recurringPlanStatus === RecurringPlanStatuses.ACTIVE && nextBillingDate && (
              <>
                {' '}
                <span>→</span> <b>{tr('FRONTEND.RECURRING.EDIT.NEXT', 'Next:') || 'Next:'}</b>{' '}
                {formatDisplayDate(nextBillingDate)}
              </>
            )}
          </span>
        </div>
      </RecurringModalCard>

      {!isCancelled && (
        <>
          <p className={cn(styles['recurring-edit-hint'], 'recurring-edit-hint')}>
            {tr(
              'FRONTEND.RECURRING.EDIT.HINT',
              'Pausing stops future payments until you resume. Cancelling stops the plan permanently.'
            )}
          </p>

          <ErrorDisplay error={[updatePlanStatusError, cancelPlanError]} />

          <div className={cn(styles['recurring-edit-actions'], 'recurring-edit-actions')}>
            <MMButton
              type='button'
              variant='dashboard'
              caption={
                isPaused
                  ? tr('FRONTEND.RECURRING.EDIT.RESUME_BUTTON', 'Resume Payment')
                  : tr('FRONTEND.RECURRING.EDIT.PAUSE_BUTTON', 'Pause Payment')
              }
              onClick={handleToggleStatus}
              loading={updatePlanStatusLoading}
              disabled={cancelPlanLoading}
              data-test='button-toggle-status'
            />
            <MMButton
              type='button'
              variant='filter'
              caption={tr('FRONTEND.RECURRING.EDIT.CANCEL_BUTTON', 'Cancel Payment')}
              onClick={handleCancelClick}
              disabled={updatePlanStatusLoading || cancelPlanLoading}
              data-test='button-cancel-plan'
            />
          </div>
        </>
      )}
    </RecurringModalSurface>
  )
}

export default RecurringEditModal
